import { Callback } from "game-common/models"
import { BLEND_MODES, Circle, Container, Graphics, LINE_CAP, LINE_JOIN, Rectangle } from "pixi.js"

import { TextRenderer } from "../text/text_renderer"
import { chamfer, GOLD, RIM_LIGHT, TIMBER_EDGE } from "./build_board"
import { CLUE_NOTCH_HALF, CLUE_NOTE_CHAMFER, ClueEdge, ClueNotch, notchBase } from "./ember_clues"
import { controlHitRect, getUiMetrics, ResponsiveHitRect } from "./ui_metrics"
import { HUD_PANEL_FILL, HUD_PANEL_RIM, HUD_PANEL_TEXT, HUD_PANEL_TEXT_MUTED } from "./ui_tokens"

// THE LOOK OF A TUTORIAL CLUE: a small cut-timber note with a notch that points at a control, and a
// pair of gold brackets that close round the control itself.
//
// It replaces a 0xff0000 box and leader -- a debugger's annotation over a timber-and-bronze HUD --
// on a rounded panel with a dusty-red rim, a blurred DropShadowFilter, a slate close disc on a row of
// its own, and a 250ms setInterval strobe. A clue interrupts play by design; this is what makes the
// interruption cost as little as it can: it is as small as its words, it is the same object as the
// chat bubbles the Lord is teaching in (avatar_hud.ts spokeImpl, the same plate, chamfer and inked
// rim), and the only thing that moves is a slow breath on the brackets.
//
// No filters and no timers (see the budget hardware target): the glow is a fat low-alpha stroke, the
// shadow a dropped silhouette, and every bit of motion is a pure pose function of elapsed time that
// gui/tutorial.ts feeds from GuiManager.updateTimer.

// ---- the note ----------------------------------------------------------------------------------

const PAD_X = 11
const PAD_Y = 8
const CHAMFER = CLUE_NOTE_CHAMFER
const SHADOW_Y = 3
const SHADOW_ALPHA = 0.38
const FILL_ALPHA = 0.97
// An inked rim, as on the chat bubble: a wide dark stroke, then a thin bronze one down its middle.
const EDGE_WIDTH = 3.5
const EDGE_ALPHA = 0.95
const RIM_WIDTH = 1.25
const MITER_LIMIT = 3 // bevels the notch's tip rather than spiking it
const TOP_LIGHT_ALPHA = 0.2

// The first line is the instruction and is read; the rest explain it and are glanced at.
const LEAD_ROLE = "label" as const
const NOTE_ROLE = "compactBody" as const
const LEAD_NOTE_GAP = 3
const NOTE_GAP = 1
// What a line measures when nothing could be (jsdom's mocked canvas), so the note never collapses.
const LEAD_LINE_H = 17
const NOTE_LINE_H = 15

// The spark before the first line: a four-point star in the brackets' gold, so the note and the
// brackets read as one object without the note needing a gold rim of its own.
const SPARK_R = 4
const SPARK_GAP = 7

// The dismiss X: a bare stroked glyph tucked in the top-right corner, level with the first line. It
// used to be the dialog close disc on a row of its own under the words, which made every note a
// whole button-row taller than its text and put the loudest shape on the panel on the one thing a
// player should do least.
const DISMISS_ARM = 3.25
const DISMISS_STROKE = 1.6
const DISMISS_GAP = 10 // between the end of the longest line and the X
const DISMISS_HIT = 11 // half-size of its desktop hit box: generous, it is a small glyph

export interface ClueCalloutProps {
    lines: string[]
    onDismiss?: Callback
}

const spark = (g: Graphics, cx: number, cy: number, r: number) => {
    const k = r * 0.3
    g.moveTo(cx, cy - r)
        .lineTo(cx + k, cy - k).lineTo(cx + r, cy)
        .lineTo(cx + k, cy + k).lineTo(cx, cy + r)
        .lineTo(cx - k, cy + k).lineTo(cx - r, cy)
        .lineTo(cx - k, cy - k)
        .closePath()
}

export class ClueCallout extends Container {
    // Child 0, so everything else is drawn over it.
    readonly backdrop: Graphics
    readonly dismiss: Graphics
    private sparkNode: Graphics
    private noteW = 0
    private noteH = 0
    private notch: ClueNotch | null = null
    private dismissHovered = false

    constructor({ lines, onDismiss }: ClueCalloutProps) {
        super()
        this.backdrop = new Graphics()
        this.addChild(this.backdrop)

        this.sparkNode = new Graphics()
        this.addChild(this.sparkNode)

        const textX = PAD_X + SPARK_R * 2 + SPARK_GAP - 2
        let y = PAD_Y
        let widest = 0
        let leadH = LEAD_LINE_H
        lines.forEach((line, index) => {
            const lead = index === 0
            const text = new TextRenderer()
                .role(lead ? LEAD_ROLE : NOTE_ROLE)
                .bold(lead)
                .color(lead ? HUD_PANEL_TEXT : HUD_PANEL_TEXT_MUTED)
                .strokeThickness(0)
                .render(line)
            if (index > 0) y += index === 1 ? LEAD_NOTE_GAP : NOTE_GAP
            text.x = textX
            text.y = y
            const h = Math.max(text.height, lead ? LEAD_LINE_H : NOTE_LINE_H)
            if (lead) leadH = h
            y += h
            widest = Math.max(widest, text.width)
            this.addChild(text)
        })

        this.noteW = Math.round(textX + widest + DISMISS_GAP + DISMISS_ARM * 2 + PAD_X - 1)
        this.noteH = Math.round(y + PAD_Y)

        // The spark sits on the first line's centre, the X level with it at the far edge.
        const leadMid = PAD_Y + leadH / 2
        this.sparkNode.beginFill(GOLD, 1)
        spark(this.sparkNode, PAD_X + SPARK_R, leadMid, SPARK_R)
        this.sparkNode.endFill()

        this.dismiss = new Graphics()
        this.dismiss.x = this.noteW - PAD_X + 1 - DISMISS_ARM
        this.dismiss.y = leadMid
        this.dismiss.interactive = true
        this.dismiss.cursor = "pointer"
        this.dismiss.hitArea = getUiMetrics().touch
            ? new ResponsiveHitRect(() => {
                const rect = controlHitRect(this.dismiss, DISMISS_HIT * 2, DISMISS_HIT * 2)
                rect.x -= DISMISS_HIT
                rect.y -= DISMISS_HIT
                return rect
            })
            : new Circle(0, 0, DISMISS_HIT)
        this.dismiss.on("mouseover", () => this.drawDismiss(true))
        this.dismiss.on("mouseout", () => this.drawDismiss(false))
        if (onDismiss) {
            this.dismiss.on("click", onDismiss)
            this.dismiss.on("tap", onDismiss)
        }
        this.drawDismiss(false)
        this.addChild(this.dismiss)

        // The note is the hover target, not its shadow or its notch.
        this.hitArea = new Rectangle(0, 0, this.noteW, this.noteH)
        this.drawBackdrop()
    }

    /** The note's own box, notch excluded, in its local coordinates. */
    noteRect = () => new Rectangle(0, 0, this.noteW, this.noteH)

    getNotch = () => this.notch

    /** Point the note at something; null takes the notch off. */
    setNotch = (notch: ClueNotch | null) => {
        this.notch = notch
        this.drawBackdrop()
    }

    private drawDismiss = (hover: boolean) => {
        this.dismissHovered = hover
        const g = this.dismiss
        g.clear()
        g.lineStyle({
            width: DISMISS_STROKE,
            color: hover ? GOLD : HUD_PANEL_TEXT_MUTED,
            alpha: hover ? 1 : 0.7,
            cap: LINE_CAP.ROUND,
        })
        g.moveTo(-DISMISS_ARM, -DISMISS_ARM).lineTo(DISMISS_ARM, DISMISS_ARM)
        g.moveTo(DISMISS_ARM, -DISMISS_ARM).lineTo(-DISMISS_ARM, DISMISS_ARM)
        g.lineStyle(0)
    }

    isDismissHovered = () => this.dismissHovered

    // The outline -- chamfered box plus the notch -- is ONE path, filled and stroked once, so the
    // rim runs unbroken round the point (the chat bubble's lesson: a separately drawn tail seams).
    private tracePath = (g: Graphics, oy: number) => {
        const w = this.noteW
        const h = this.noteH
        const c = CHAMFER
        const n = this.notch
        const notchOn = (edge: ClueEdge) => n && n.edge === edge
        const base = (len: number, along: number) => notchBase(len, along)
        g.moveTo(c, oy)
        if (notchOn("top")) {
            const b = base(w, n.tipX)
            g.lineTo(b - CLUE_NOTCH_HALF, oy).lineTo(n.tipX, n.tipY + oy).lineTo(b + CLUE_NOTCH_HALF, oy)
        }
        g.lineTo(w - c, oy).lineTo(w, oy + c)
        if (notchOn("right")) {
            const b = base(h, n.tipY)
            g.lineTo(w, oy + b - CLUE_NOTCH_HALF).lineTo(n.tipX, n.tipY + oy).lineTo(w, oy + b + CLUE_NOTCH_HALF)
        }
        g.lineTo(w, oy + h - c).lineTo(w - c, oy + h)
        if (notchOn("bottom")) {
            const b = base(w, n.tipX)
            g.lineTo(b + CLUE_NOTCH_HALF, oy + h).lineTo(n.tipX, n.tipY + oy).lineTo(b - CLUE_NOTCH_HALF, oy + h)
        }
        g.lineTo(c, oy + h).lineTo(0, oy + h - c)
        if (notchOn("left")) {
            const b = base(h, n.tipY)
            g.lineTo(0, oy + b + CLUE_NOTCH_HALF).lineTo(n.tipX, n.tipY + oy).lineTo(0, oy + b - CLUE_NOTCH_HALF)
        }
        g.lineTo(0, oy + c)
        g.closePath()
    }

    private drawBackdrop = () => {
        const g = this.backdrop
        g.clear()
        // Dropped silhouette, straight down: a diagonal offset ghosts a second notch beside the real one.
        g.lineStyle(0)
        g.beginFill(0x000000, SHADOW_ALPHA)
        this.tracePath(g, SHADOW_Y)
        g.endFill()

        g.lineStyle({ width: EDGE_WIDTH, color: TIMBER_EDGE, alpha: EDGE_ALPHA, join: LINE_JOIN.MITER, miterLimit: MITER_LIMIT })
        g.beginFill(HUD_PANEL_FILL, FILL_ALPHA)
        this.tracePath(g, 0)
        g.endFill()
        g.lineStyle({ width: RIM_WIDTH, color: HUD_PANEL_RIM, alpha: 1, join: LINE_JOIN.MITER, miterLimit: MITER_LIMIT })
        this.tracePath(g, 0)

        // One lit edge along the top, so the note has a direction the light falls from (build_board).
        g.lineStyle(1, RIM_LIGHT, TOP_LIGHT_ALPHA)
        g.moveTo(CHAMFER + 1, 2.5).lineTo(this.noteW - CHAMFER - 1, 2.5)
        g.lineStyle(0)
    }
}

// ---- the brackets ------------------------------------------------------------------------------

// Clear air between the control and the brackets at rest, and how far they breathe out from there.
const RING_PAD = 4
const RING_BREATH = 2.5
const RING_BREATH_MS = 1700
// The brackets arrive from further out and close onto the control: the eye follows them in.
const RING_ARRIVE_FROM = 14
const RING_ARRIVE_MS = 340
const RING_LEAVE_TO = 6
const RING_STROKE = 2
const RING_INK = 4 // the dark stroke under the gold, so it holds over a light control or grass
const RING_GLOW = 6
const BRACKET_CUT = 3 // each corner is chamfered, like everything else on the board

export const CLUE_LEAVE_MS = 170

export interface ClueRingPose {
    pad: number
    alpha: number
    glow: number
}

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)
const clamp01 = (t: number) => Math.max(0, Math.min(1, t))

/**
 * The brackets at `shownMs` since they appeared (and `leftMs` since they started to go, if they
 * have). The breath is a slow sine, not a blink -- a strobe hides the very thing it is pointing at,
 * which is what the old 250ms throb did to the control behind it.
 */
export const clueRingPose = (shownMs: number, leftMs?: number): ClueRingPose => {
    const breath = 0.5 - 0.5 * Math.cos((2 * Math.PI * Math.max(0, shownMs)) / RING_BREATH_MS)
    const resting = RING_PAD + RING_BREATH * breath
    const arrive = easeOutCubic(clamp01(shownMs / RING_ARRIVE_MS))
    let pad = resting + RING_ARRIVE_FROM * (1 - arrive)
    let alpha = clamp01(shownMs / (RING_ARRIVE_MS * 0.5))
    const glow = 0.55 + 0.45 * breath
    if (leftMs !== undefined) {
        const t = clamp01(leftMs / CLUE_LEAVE_MS)
        pad += RING_LEAVE_TO * easeOutCubic(t)
        alpha *= 1 - t
    }
    return { pad, alpha, glow }
}

/**
 * The note's entrance and exit: a short fade while it slides the last few pixels TOWARD the control,
 * so it reads as being put down beside it rather than appearing out of nowhere.
 */
export const clueNotePose = (shownMs: number, leftMs?: number) => {
    const t = easeOutCubic(clamp01(shownMs / 220))
    let alpha = t
    let slide = 7 * (1 - t)
    if (leftMs !== undefined) {
        const out = clamp01(leftMs / CLUE_LEAVE_MS)
        alpha *= 1 - out
        slide += 3 * out
    }
    return { alpha, slide }
}

export interface ClueRingTarget {
    w: number
    h: number
    round?: boolean
    // Light the control up instead of bracketing it: see ClueLight. For a control under a spotlight.
    lit?: boolean
}

/** What marks a clue's control: brackets (ClueRing) or a lit frame (ClueLight). */
export interface ClueMark extends Graphics {
    readonly target: ClueRingTarget
    poseAt: (shownMs: number, leftMs?: number) => void
}

/** Gold brackets round a control, drawn at the control's top-left. */
export class ClueRing extends Graphics implements ClueMark {
    readonly target: ClueRingTarget

    constructor(target: ClueRingTarget) {
        super()
        this.target = target
        this.pose(clueRingPose(0))
    }

    poseAt = (shownMs: number, leftMs?: number) => this.pose(clueRingPose(shownMs, leftMs))

    pose = ({ pad, alpha, glow }: ClueRingPose) => {
        this.clear()
        this.alpha = alpha
        const strokes: [number, number, number][] = [
            [RING_GLOW, GOLD, 0.16 * glow],
            [RING_INK, TIMBER_EDGE, 0.7],
            [RING_STROKE, GOLD, 1],
        ]
        for (const [width, color, a] of strokes) {
            this.lineStyle({ width, color, alpha: a, cap: LINE_CAP.SQUARE, join: LINE_JOIN.MITER })
            if (this.target.round) this.traceArcs(pad)
            else this.traceBrackets(pad)
        }
        this.lineStyle(0)
    }

    // Four corners of a chamfered frame `pad` outside the control.
    private traceBrackets = (pad: number) => {
        const { w, h } = this.target
        const x0 = -pad
        const y0 = -pad
        const x1 = w + pad
        const y1 = h + pad
        const arm = Math.max(6, Math.min(12, Math.min(w, h) * 0.35)) + pad * 0.5
        const k = BRACKET_CUT
        this.moveTo(x0, y0 + arm).lineTo(x0, y0 + k).lineTo(x0 + k, y0).lineTo(x0 + arm, y0)
        this.moveTo(x1 - arm, y0).lineTo(x1 - k, y0).lineTo(x1, y0 + k).lineTo(x1, y0 + arm)
        this.moveTo(x1, y1 - arm).lineTo(x1, y1 - k).lineTo(x1 - k, y1).lineTo(x1 - arm, y1)
        this.moveTo(x0 + arm, y1).lineTo(x0 + k, y1).lineTo(x0, y1 - k).lineTo(x0, y1 - arm)
    }

    // A round control (the wave dial) gets four arcs on the diagonals instead -- between the dial's
    // own quarter notches, so the two sets of marks interleave rather than sit on top of each other.
    private traceArcs = (pad: number) => {
        const { w, h } = this.target
        const cx = w / 2
        const cy = h / 2
        const r = Math.max(w, h) / 2 + pad
        const half = (22 * Math.PI) / 180
        for (let q = 0; q < 4; q++) {
            const mid = Math.PI / 4 + (q * Math.PI) / 2
            this.moveTo(cx + Math.cos(mid - half) * r, cy + Math.sin(mid - half) * r)
            this.arc(cx, cy, r, mid - half, mid + half)
        }
    }
}

// ---- the spotlight -----------------------------------------------------------------------------

// A clue inside the inventory panel dims the panel and lights its control up. The note on its own
// was the same dark timber as the panel it sat on, laid over the panel's own title, so it read as
// the panel's heading rather than as an instruction; and bracketing a grey button left the button
// grey. Under a spotlight the control is the one lit thing on the board, and the note stands on a
// darker ground than it is.

export interface ClueRect {
    x: number
    y: number
    w: number
    h: number
}

/** The area a spotlight dims, in its host's coordinates: a panel's own chamfered board. */
export interface ClueSpotlightArea extends ClueRect {
    chamfer?: number
    // For whatever lies over the area out of the scrim's reach -- the modal seats its close disc
    // above the panel, and the crafting search box is a DOM field over the canvas. Called with the
    // brightness the scrim leaves the board at (1 = undimmed), so the owner can darken them to match.
    onDim?: (brightness: number) => void
}

const SCRIM_COLOR = 0x060403
const SCRIM_ALPHA = 0.7
// Clear air round the control inside the cut, so its lit frame stands in the light too.
export const SPOTLIGHT_HOLE_PAD = 6
const HOLE_CHAMFER = 5
// The panel dims a little slower than the note lands, and lifts as fast as a clue leaves.
export const SPOTLIGHT_IN_MS = 260
export const SPOTLIGHT_OUT_MS = CLUE_LEAVE_MS

/**
 * The cuts a spotlight makes for these controls: padded, kept inside the area (a hover menu can
 * hang over the panel's edge), and merged where they would overlap, since overlapping holes do not
 * triangulate. A control wholly outside the area needs no cut: nothing is dimmed over it.
 */
export const spotlightHoles = (area: ClueRect, targets: ClueRect[], pad = SPOTLIGHT_HOLE_PAD): ClueRect[] => {
    const inset = HOLE_CHAMFER
    const clipped: ClueRect[] = []
    for (const t of targets) {
        const x0 = Math.max(area.x + inset, t.x - pad)
        const y0 = Math.max(area.y + inset, t.y - pad)
        const x1 = Math.min(area.x + area.w - inset, t.x + t.w + pad)
        const y1 = Math.min(area.y + area.h - inset, t.y + t.h + pad)
        if (x1 > x0 && y1 > y0) clipped.push({ x: x0, y: y0, w: x1 - x0, h: y1 - y0 })
    }
    const overlaps = (a: ClueRect, b: ClueRect) =>
        a.x <= b.x + b.w && b.x <= a.x + a.w && a.y <= b.y + b.h && b.y <= a.y + a.h
    let merged = clipped
    for (let changed = true; changed;) {
        changed = false
        const next: ClueRect[] = []
        for (const rect of merged) {
            const hit = next.findIndex(other => overlaps(rect, other))
            if (hit < 0) {
                next.push(rect)
                continue
            }
            const o = next[hit]
            const x = Math.min(o.x, rect.x)
            const y = Math.min(o.y, rect.y)
            next[hit] = { x, y, w: Math.max(o.x + o.w, rect.x + rect.w) - x, h: Math.max(o.y + o.h, rect.y + rect.h) - y }
            changed = true
        }
        merged = next
    }
    return merged
}

/** The dimming over a panel, with a cut for each control being taught. Its alpha is the level. */
export class ClueScrim extends Graphics {
    area?: ClueSpotlightArea
    private brightness = 1

    /** How dark the board is, 0..1: the scrim's alpha, and what the area's owner is told. */
    setLevel = (level: number) => {
        this.alpha = level
        this.tellOwner(1 - SCRIM_ALPHA * level)
    }

    /** Let the owner put back whatever it dimmed. */
    undim = () => this.tellOwner(1)

    private tellOwner = (brightness: number) => {
        if (brightness === this.brightness) return
        this.brightness = brightness
        this.area?.onDim?.(brightness)
    }

    draw = (area: ClueSpotlightArea, holes: ClueRect[]) => {
        this.area = area
        this.clear()
        this.lineStyle(0)
        this.beginFill(SCRIM_COLOR, SCRIM_ALPHA)
        chamfer(this, area.x, area.y, area.w, area.h, area.chamfer || 0)
        for (const hole of holes) {
            this.beginHole()
            chamfer(this, hole.x, hole.y, hole.w, hole.h, HOLE_CHAMFER)
            this.endHole()
        }
        this.endFill()
    }
}

// The lit frame: hugs the control, arrives from further out like the brackets do, and breathes far
// less than they do -- a full frame swelling and shrinking round a button reads as a wobble.
const LIGHT_PAD = 2.5
const LIGHT_BREATH = 1
const LIGHT_ARRIVE_FROM = 12
const LIGHT_CUT = 4
const LIGHT_WARM = 0xffb347
// Light spilling off the control onto the dimmed board round it: widest and faintest first. ADDED,
// like the light it is, so it warms what it falls on instead of laying a film over it.
const LIGHT_SPILL: [number, number][] = [[18, 0.07], [11, 0.1], [6, 0.16]]
// The control's face, lit: added over it, so its own label brightens with it rather than being
// washed out -- a grey "Craft" comes up cream on warm timber.
const LIGHT_FACE_ALPHA = 0.3

export const clueLightPose = (shownMs: number, leftMs?: number): ClueRingPose => {
    const breath = 0.5 - 0.5 * Math.cos((2 * Math.PI * Math.max(0, shownMs)) / RING_BREATH_MS)
    const arrive = easeOutCubic(clamp01(shownMs / RING_ARRIVE_MS))
    let pad = LIGHT_PAD + LIGHT_BREATH * breath + LIGHT_ARRIVE_FROM * (1 - arrive)
    let alpha = clamp01(shownMs / (RING_ARRIVE_MS * 0.5))
    const glow = 0.6 + 0.4 * breath
    if (leftMs !== undefined) {
        const t = clamp01(leftMs / CLUE_LEAVE_MS)
        pad += RING_LEAVE_TO * easeOutCubic(t)
        alpha *= 1 - t
    }
    return { pad, alpha, glow }
}

/** A control lit up: warm light on its face, a gold rim round it, and light spilling past the rim. */
export class ClueLight extends Graphics implements ClueMark {
    readonly target: ClueRingTarget
    // The light itself, on its own ADD layer under the rim.
    private light: Graphics

    constructor(target: ClueRingTarget) {
        super()
        this.target = target
        this.light = new Graphics()
        this.light.blendMode = BLEND_MODES.ADD
        this.addChild(this.light)
        this.pose(clueLightPose(0))
    }

    poseAt = (shownMs: number, leftMs?: number) => this.pose(clueLightPose(shownMs, leftMs))

    pose = ({ pad, alpha, glow }: ClueRingPose) => {
        const { w, h } = this.target
        // A round control (the close disc) gets rings, not a box round its bounds.
        const round = this.target.round
        const outline = (g: Graphics, out: number) => {
            if (round) g.drawCircle(w / 2, h / 2, Math.max(w, h) / 2 + out)
            else chamfer(g, -out, -out, w + out * 2, h + out * 2, out > 0 ? LIGHT_CUT : LIGHT_CUT - 1)
        }
        this.alpha = alpha
        const light = this.light
        light.clear()
        light.lineStyle(0)
        light.beginFill(LIGHT_WARM, LIGHT_FACE_ALPHA * glow)
        outline(light, 0)
        light.endFill()
        for (const [width, a] of LIGHT_SPILL) {
            light.lineStyle({ width, color: LIGHT_WARM, alpha: a * glow, join: LINE_JOIN.MITER })
            outline(light, pad)
        }
        light.lineStyle(0)
        this.clear()
        this.lineStyle({ width: RING_INK, color: TIMBER_EDGE, alpha: 0.7, join: LINE_JOIN.MITER })
        outline(this, pad)
        this.lineStyle({ width: RING_STROKE, color: GOLD, alpha: 1, join: LINE_JOIN.MITER })
        outline(this, pad)
        this.lineStyle(0)
    }
}
